import { Button, ButtonVariants, IUserLogin, NewUserWithOrganization, Sessions, SsoCompleteSignup, SsoPendingSignup, useAuth, UnauthorizedError, UserLogin, Users } from "@app/shared";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pages } from "..";
import { useModel } from "../../../hooks";
import { NoAuthAccountLayout } from "../../layouts";
import { Input, InputRow, PasswordInput } from "../../shared";

export const CreateAccount: React.FC = () => {
    const [query] = useSearchParams();
    return query.get("sso") === "1" ? <CreateAccountSso /> : <CreateAccountClassic />;
};

const CreateAccountClassic: React.FC = () => {
    const [user, helpers] = useModel(NewUserWithOrganization);
    const auth = useAuth();
    const navigate = useNavigate();

    const handleCreateOnClick = async () => {
        helpers.validateWithAction(async () => {
            await Users.createUser(user as NewUserWithOrganization);
            try {
                await auth.logIn(new UserLogin({ email: user.emailAddress, password: user.password } as IUserLogin));
            } catch (e) {
                console.error("Error logging in... continuing to navigate to the locations page", e);
            }
            navigate(Pages.ViewFacilityGroups.getUrl(), { replace: true });
        });
    };

    const handleCancelOnClick = () => navigate(Pages.Login.getUrl());

    return (
        <NoAuthAccountLayout>
            <div className={helpers.modelContainerClassNames}>
                <h2>New User Details</h2>
                <p>Please provide the following details about you:</p>
                <helpers.ErrorSummary />
                <InputRow>
                    <Input label="First Name" {...helpers.bindingsFor("firstName")} />
                    <Input label="Last Name" {...helpers.bindingsFor("lastName")} />
                    <Input label="Email" {...helpers.bindingsFor("emailAddress")} />
                    <PasswordInput label="Password" {...helpers.bindingsFor("password")} />
                    <PasswordInput label="Confirm Password" {...helpers.bindingsFor("confirmPassword")} />
                </InputRow>
                <h2 className="pt-5">Organization Details</h2>
                <p>If your organization already has an account, you may want to consider having someone send you an invite to the organization.</p>
                <InputRow>
                    <Input label="Organization Name" {...helpers.bindingsFor("organizationName")} />
                    <Input label="Phone" {...helpers.bindingsFor("organizationPhoneNumber")} />
                    <Input label="Address" {...helpers.bindingsFor("organizationAddress")} />
                    <Input label="Address Line 2" {...helpers.bindingsFor("organizationAddress2")} />
                    <Input label="City" {...helpers.bindingsFor("organizationCity")} />
                    <Input label="State" {...helpers.bindingsFor("organizationState")} />
                    <Input label="Zip" {...helpers.bindingsFor("organizationZipcode")} />
                </InputRow>
                <FormButtons onCreate={handleCreateOnClick} onCancel={handleCancelOnClick} />
            </div>
        </NoAuthAccountLayout>
    );
};

const CreateAccountSso: React.FC = () => {
    const [org, helpers] = useModel(SsoCompleteSignup);
    const auth = useAuth();
    const navigate = useNavigate();
    const [pending, setPending] = useState<SsoPendingSignup | undefined>();
    const [loadError, setLoadError] = useState<string | undefined>();

    useEffect(() => {
        let cancelled = false;
        Sessions.ssoPending()
            .then((p) => {
                if (cancelled || !p) return;
                setPending(p);
                if (p.orgNameSuggestion) {
                    helpers.updateModel("organizationName", p.orgNameSuggestion);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(err instanceof UnauthorizedError
                    ? "Your SSO session has expired. Please sign in again."
                    : "We couldn't find a pending SSO signup. Please start sign-in again.");
            });
        return () => { cancelled = true; };
        // helpers from useModel is recreated each render and not safe to depend on; effect runs once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateOnClick = async () => {
        helpers.validateWithAction(async () => {
            try {
                await Sessions.ssoCompleteSignup(org as SsoCompleteSignup);
            } catch (err) {
                if (err instanceof UnauthorizedError) {
                    helpers.setSummaryError(["Your SSO session has expired. Please sign in again."]);
                    return;
                }
                throw err;
            }
            await auth.reload?.();
            navigate(Pages.ViewFacilityGroups.getUrl(), { replace: true });
        });
    };

    const handleCancelOnClick = () => navigate(Pages.Login.getUrl());
    const handleRetrySsoClick = () => {
        const apiRoot = (process.env.REACT_APP_CORE_API_ROOT_URL ?? "").replace(/\/$/, "");
        window.location.href = `${apiRoot}/api/session/sso/login?returnUrl=${encodeURIComponent("/")}`;
    };

    return (
        <NoAuthAccountLayout>
            <div className={helpers.modelContainerClassNames}>
                {loadError ? (
                    <>
                        <h2>SSO signup unavailable</h2>
                        <p className="text-red-600 text-sm">{loadError}</p>
                        <div className="flex pt-5 gap-5 place-items-center justify-center">
                            <Button onClick={handleRetrySsoClick}>Sign in with SSO</Button>
                            <Button variant={ButtonVariants.Secondary} onClick={handleCancelOnClick}>Cancel</Button>
                        </div>
                    </>
                ) : (
                    <>
                        <h2>Welcome{pending?.firstName ? `, ${pending.firstName}` : ""}</h2>
                        <p>You're signed in as <strong>{pending?.email}</strong>. Tell us about your organization to finish setting up your account.</p>
                        <helpers.ErrorSummary />
                        <h2>Organization Details</h2>
                        <InputRow>
                            <Input label="Organization Name" {...helpers.bindingsFor("organizationName")} />
                            <Input label="Phone" {...helpers.bindingsFor("organizationPhoneNumber")} />
                            <Input label="Address" {...helpers.bindingsFor("organizationAddress")} />
                            <Input label="Address Line 2" {...helpers.bindingsFor("organizationAddress2")} />
                            <Input label="City" {...helpers.bindingsFor("organizationCity")} />
                            <Input label="State" {...helpers.bindingsFor("organizationState")} />
                            <Input label="Zip" {...helpers.bindingsFor("organizationZipcode")} />
                        </InputRow>
                        <FormButtons onCreate={handleCreateOnClick} onCancel={handleCancelOnClick} />
                    </>
                )}
            </div>
        </NoAuthAccountLayout>
    );
};

const FormButtons: React.FC<{ onCreate: () => void; onCancel: () => void }> = ({ onCreate, onCancel }) => (
    <div className="flex pt-5 gap-5 place-items-center justify-center">
        <Button onClick={onCreate}>Create</Button>
        <Button variant={ButtonVariants.Secondary} onClick={onCancel}>Cancel</Button>
    </div>
);
